import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';

export default function Upload() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Role guard (belt-and-suspenders; route guard already applies)
  if (user?.role !== 'caddie') {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center text-gray-500">
        <p>Only caddies can post photos.</p>
      </div>
    );
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  }

  function handleDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith('image/')) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { setError('Please select an image.'); return; }
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('caption', caption);
      const post = await api.posts.create(fd);
      navigate(`/posts/${post.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <h1 className="text-xl font-bold text-gray-900 mb-6">New Post</h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Drop zone */}
        <div
          onClick={() => !preview && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={[
            'relative rounded-xl border-2 border-dashed overflow-hidden transition-colors',
            preview ? 'border-transparent cursor-default' : 'border-gray-300 hover:border-brand-400 cursor-pointer bg-gray-50',
          ].join(' ')}
        >
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Preview" className="w-full max-h-80 object-contain bg-black" />
              <button
                type="button"
                onClick={() => { setPreview(null); setFile(null); }}
                className="absolute top-2 right-2 bg-white/80 backdrop-blur rounded-full px-3 py-1 text-xs font-medium text-gray-700 hover:bg-white shadow"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="py-16 text-center">
              <div className="text-4xl mb-2">📸</div>
              <p className="text-sm text-gray-500">Click or drag &amp; drop an image</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP — max 10 MB</p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Caption */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Caption (optional)</label>
          <textarea
            className="input resize-none"
            rows={3}
            placeholder="Write a caption…"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={500}
          />
          <p className="text-xs text-gray-400 mt-1">{caption.length}/500</p>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary flex-1">
            Cancel
          </button>
          <button type="submit" disabled={loading || !file} className="btn-primary flex-1">
            {loading ? 'Uploading…' : 'Share Post'}
          </button>
        </div>
      </form>
    </div>
  );
}
